export interface DashboardNarration {
  id: string;
  title: string;
  narration: string;
  triggerPoint: 'onLoad' | 'onInteraction' | 'onCompletion';
}

/**
 * Narration script for the JIMI Wellness Platform interactive dashboards
 * 
 * These narrations guide users through the interactive dashboard experience,
 * explaining features and providing context for the demonstration.
 */
export const dashboardNarrationScript: DashboardNarration[] = [
  // Resilience Dashboard Narrations
  {
    id: "resilience-dashboard-intro",
    title: "Resilience Dashboard Introduction",
    narration: "Welcome to the Resilience Dashboard, the first pillar of the JIMI Wellness Platform. This dashboard provides clinicians with personalized wellness activities, health tracking, and AI-guided support. The interface is designed to be intuitive and accessible, allowing clinicians to quickly engage with wellness activities between patient visits.",
    triggerPoint: "onLoad"
  },
  {
    id: "resilience-wellness-metrics",
    title: "Wellness Metrics Overview",
    narration: "The Wellness Metrics section displays key indicators of clinician well-being. These metrics are tracked over time, allowing clinicians to monitor their progress and identify areas that need attention. The AI companion uses this data to provide personalized recommendations and support.",
    triggerPoint: "onInteraction"
  },
  {
    id: "resilience-recommended-activities",
    title: "Recommended Activities",
    narration: "JIMI's AI companion analyzes wellness data to recommend personalized activities. These quick interventions are designed to fit into a busy clinical schedule, typically taking just 1-5 minutes to complete. Activities are categorized by type and benefit, allowing clinicians to choose what works best for their current needs.",
    triggerPoint: "onInteraction"
  },
  {
    id: "resilience-activity-completion",
    title: "Activity Completion",
    narration: "When clinicians complete activities, their progress is tracked and reflected in their wellness metrics. The platform celebrates these small wins, reinforcing positive habits and building resilience over time. Regular engagement with these activities has been shown to significantly reduce burnout symptoms.",
    triggerPoint: "onCompletion"
  },
  
  // Activity Narrations
  {
    id: "mindful-breathing-intro",
    title: "Mindful Breathing Introduction",
    narration: "Mindful breathing is a quick, evidence-based technique to reduce stress between patient visits. This 2-minute exercise helps clinicians reset their mental state, improve focus, and reduce physiological stress responses. Research shows that even brief mindfulness practices can significantly reduce burnout symptoms when practiced regularly.",
    triggerPoint: "onLoad"
  },
  {
    id: "gratitude-journal-intro",
    title: "Gratitude Journal Introduction",
    narration: "The gratitude journal activity encourages clinicians to reflect on positive aspects of their practice and personal life. This 3-minute exercise helps shift attention from stressors to sources of meaning and satisfaction. Regular gratitude practice has been linked to improved mood, better sleep quality, and increased resilience to workplace challenges.",
    triggerPoint: "onLoad"
  },
  {
    id: "quick-stretch-intro",
    title: "Quick Stretch Introduction",
    narration: "This 3-minute stretch routine is specifically designed for clinicians who spend long hours standing or at a computer. These targeted stretches help reduce physical tension, improve circulation, and prevent repetitive strain injuries. Regular movement breaks throughout the day have been shown to improve both physical comfort and cognitive function.",
    triggerPoint: "onLoad"
  },
  {
    id: "peer-connection-intro",
    title: "Peer Connection Introduction",
    narration: "The peer connection activity facilitates structured check-ins between colleagues to share challenges and successes. This 5-minute exercise helps build supportive professional relationships and reduce isolation. Research shows that strong peer connections are one of the most protective factors against burnout in healthcare settings.",
    triggerPoint: "onLoad"
  },
  {
    id: "clinical-reflection-intro",
    title: "Clinical Reflection Introduction",
    narration: "Clinical reflection is a structured 5-minute exercise to enhance learning from patient encounters. This activity helps clinicians process challenging cases, celebrate successes, and identify learning opportunities. Regular reflective practice has been shown to improve clinical decision-making and increase professional satisfaction.",
    triggerPoint: "onLoad"
  },
  {
    id: "stress-check-intro",
    title: "Stress Check Introduction",
    narration: "The stress check-in is a quick 1-minute assessment of current stress levels with personalized recommendations. This activity helps clinicians develop awareness of stress patterns and respond proactively. The AI companion uses this data to suggest appropriate interventions and track stress trends over time.",
    triggerPoint: "onLoad"
  },
  
  // Activity Step Narrations
  {
    id: "activity-step-guidance",
    title: "Activity Step Guidance",
    narration: "Each activity is broken down into simple steps, making it easy to follow along. The progress bar shows your advancement through the activity. Take your time with each step, focusing on quality rather than speed. These activities are designed to be effective even when completed in brief moments between clinical responsibilities.",
    triggerPoint: "onInteraction"
  },
  {
    id: "activity-completion",
    title: "Activity Completion",
    narration: "Congratulations on completing this wellness activity! Even these brief interventions can have a significant positive impact when practiced regularly. Your progress has been recorded, and this data will help JIMI provide increasingly personalized recommendations over time. Remember that consistent small actions lead to meaningful improvements in well-being.",
    triggerPoint: "onCompletion"
  }
];

export default dashboardNarrationScript;
